import { Component, For, Show, createSignal, createMemo } from 'solid-js'
import { AppSchema } from '/common/types/schema'
import Modal from '/web/shared/Modal'
import { i18nStore, chatStore } from '../../../store'
import { getAssetUrl, toDuration } from '/web/shared/util'
import { useNavigate } from '@solidjs/router'
import { Pill } from '/web/shared/Card'
import Button from '/web/shared/Button'
import { Star, MessageSquare, Clock, User, ChevronRight, Send } from 'lucide-solid'
import Tabs, { useTabs } from '/web/shared/Tabs'
import { charsApi } from '/web/store/data/chars'
import { toastStore } from '/web/store/toasts'
import { getStore } from '/web/store/create'

export const CharacterDetailModal: Component<{
  char: AppSchema.Character & { chat?: AppSchema.Chat }
  show: boolean
  close: () => void
}> = (props) => {
  const i18n = i18nStore((s) => s)
  const nav = useNavigate()
  
  const [detail, setDetail] = createSignal<any>(null)
  const [comments, setComments] = createSignal<AppSchema.CharacterComment[]>([])
  const [commentText, setCommentText] = createSignal('')
  const [isSubmitting, setIsSubmitting] = createSignal(false)
  const [similarChars, setSimilarChars] = createSignal<AppSchema.Character[]>([])

  const tabs = useTabs(['profile', 'comments', 'similar'])
  const tabLabels = ['个人资料', '评论', '相似']

  // 加载详情和评论
  createMemo(() => {
    if (props.show && props.char?._id) {
      loadDetail()
      loadComments()
      loadSimilar()
    }
  })

  const loadDetail = async () => {
    const res = await charsApi.getEnhancedDetail(props.char._id)
    if (res.result) setDetail(res.result)
  }

  const loadComments = async () => {
    const res = await charsApi.getComments(props.char._id)
    if (res.result) setComments(res.result.comments)
  }

  const loadSimilar = async () => {
    // 相似推荐逻辑：获取相同标签的角色
    const res = await charsApi.getCharacters()
    if (res.result) {
      const currentTags = props.char.tags || []
      const filtered = res.result.characters
        .filter((c: any) => {
          if (c._id === props.char._id) return false
          // 如果当前角色没有标签，则返回所有其他角色
          if (currentTags.length === 0) return true
          // 检查是否有重合的标签
          const charTags = c.tags || []
          return currentTags.some(tag => charTags.includes(tag))
        })
        .sort(() => 0.5 - Math.random())
        .slice(0, 4)
      
      // 如果按标签没找到，就保底随机给几个
      if (filtered.length === 0) {
        const fallback = res.result.characters
          .filter((c: any) => c._id !== props.char._id)
          .sort(() => 0.5 - Math.random())
          .slice(0, 4)
        setSimilarChars(fallback)
      } else {
        setSimilarChars(filtered)
      }
    }
  }

  const toggleFollow = async () => {
    if (!detail()?.userId) return
    const isFollowing = detail()?.isFollowingCreator
    const res = isFollowing 
      ? await charsApi.unfollowCreator(detail().userId)
      : await charsApi.followCreator(detail().userId)
    
    if (res.result) {
      setDetail({ ...detail(), isFollowingCreator: !isFollowing })
      toastStore.success(isFollowing ? '已取消关注' : '关注成功')
    }
  }

  const toggleFavorite = async () => {
    const isFavorited = detail()?.isFavorited
    const res = await charsApi.setFavorite(props.char._id, !isFavorited)
    if (res.result) {
      setDetail({ ...detail(), isFavorited: !isFavorited })
      toastStore.success(isFavorited ? '已取消收藏' : '收藏成功')
    }
  }

  const submitComment = async () => {
    if (!commentText().trim() || isSubmitting()) return
    setIsSubmitting(true)
    const res = await charsApi.addComment(props.char._id, commentText())
    setIsSubmitting(false)
    if (res.result) {
      setCommentText('')
      loadComments()
      toastStore.success('评论发表成功')
    }
  }

  // 点击“开始聊天”：如果已有聊天，直接进入；否则创建新聊天后直接进入聊天页
  const startChat = () => {
    if (!props.char) return

    // 已经有聊天，直接跳转
    if (props.char.chat) {
      nav(`/chat/${props.char.chat._id}`)
      props.close()
      return
    }

    // 没有聊天：使用 chatStore.createChat 直接创建并跳到 /chat/:id
    const userStore = getStore('user')
    const user = userStore.getState().user
    const presetId = user?.defaultPreset

    chatStore.createChat(
      props.char._id,
      {
        name: new Date().toLocaleString(),
        useOverrides: false,
        genPreset: presetId,
        mode: 'standard',
      },
      (chatId) => {
        nav(`/chat/${chatId}`)
        props.close()
      }
    )
  }

  const charStats = createMemo(() => {
    if (!props.char) return { roleChars: '0', pluginChars: '0', dialogues: 0, createdAt: '' }
    const personaWords = props.char.persona?.kind === 'text' ? props.char.persona.attributes.text[0]?.length || 0 : 0
    const scenarioWords = props.char.scenario?.length || 0
    const greetingWords = props.char.greeting?.length || 0
    
    return {
      roleChars: ((personaWords + scenarioWords) / 1000).toFixed(2) + 'k',
      pluginChars: (greetingWords / 1000).toFixed(2) + 'k',
      dialogues: (props.char as any).chats || 0,
      createdAt: props.char.createdAt ? toDuration(new Date(props.char.createdAt)) : ''
    }
  })

  return (
    <Modal
      show={props.show && !!props.char}
      close={props.close}
      maxWidth="half"
      maxHeight
      contentClass="!p-0"
      dismissable
    >
      <Show when={props.char}>
        <div class="relative w-full bg-900 text-base">
          {/* Header Image Area */}
          <div class="relative h-64 w-full overflow-hidden">
            <img
              src={getAssetUrl(props.char.avatar!)}
              class="h-full w-full object-cover blur-sm opacity-50"
            />
            <div class="absolute inset-0 flex items-center justify-center p-4">
              <img
                src={getAssetUrl(props.char.avatar!)}
                class="h-full rounded-lg object-contain shadow-xl"
              />
            </div>
          </div>

          {/* Stats Row */}
          <div class="grid grid-cols-4 gap-2 border-b border-[var(--bg-700)] p-4 text-center">
            <div>
              <div class="text-lg font-bold">{charStats().roleChars}</div>
              <div class="text-xs text-500">角色字元</div>
            </div>
            <div>
              <div class="text-lg font-bold">{charStats().pluginChars}</div>
              <div class="text-xs text-500">插件字元</div>
            </div>
            <div>
              <div class="text-lg font-bold">{detail()?.chatCount || charStats().dialogues}</div>
              <div class="text-xs text-500">对话数</div>
            </div>
            <div>
              <div class="text-lg font-bold">{charStats().createdAt}</div>
              <div class="text-xs text-500">创作于</div>
            </div>
          </div>

          {/* Tabs */}
          <div class="px-4 pt-2 flex border-b border-[var(--bg-700)]">
            <For each={tabLabels}>
              {(label, i) => (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tabs.select(i());
                  }}
                  class={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors relative z-10 ${
                    tabs.selected() === i()
                      ? 'text-pink-500 border-pink-500'
                      : 'text-gray-400 border-transparent hover:text-gray-200'
                  }`}
                >
                  {label}
                </button>
              )}
            </For>
          </div>

          <div class="p-4 space-y-6 overflow-y-auto max-h-[60vh]">
            <Show when={tabs.selected() === 0}>
              {/* Author Info */}
              <section>
                <div class="flex items-center justify-between mb-2">
                  <h3 class="text-lg font-bold">創作訊息</h3>
                  <div class="text-pink-500 flex items-center text-sm cursor-pointer" onClick={() => nav(`/profile/${props.char.userId}`)}>
                    作者專頁 <ChevronRight size={16} />
                  </div>
                </div>
                <div class="bg-800 rounded-xl p-3 flex items-center justify-between border border-pink-500/30">
                  <div class="flex items-center gap-3">
                    <div class="w-12 h-12 rounded-full overflow-hidden bg-700">
                      <Show when={detail()?.creatorProfile?.avatar || props.char.avatar} fallback={<User size={24} />}>
                          <img src={getAssetUrl(detail()?.creatorProfile?.avatar || props.char.avatar!)} class="w-full h-full object-cover" />
                      </Show>
                    </div>
                    <div>
                      <div class="font-bold flex items-center gap-1">
                        {detail()?.creatorProfile?.handle || props.char.creator || '匿名作者'}
                        <span class="bg-yellow-500 text-[10px] px-1 rounded text-black font-black">lv{detail()?.creatorProfile?.level || 1}</span>
                      </div>
                      <div class="text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <User size={10} /> {detail()?.creatorProfile?.followerCount || 0}
                      </div>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={toggleFollow}
                    class={`!rounded-full !px-4 ${detail()?.isFollowingCreator ? '!bg-gray-600 !text-white' : '!bg-pink-500 !text-white'}`}
                  >
                    {detail()?.isFollowingCreator ? '已关注' : '关注'}
                  </Button>
                </div>
                <p class="text-xs text-500 mt-2 px-1">{detail()?.creatorProfile?.bio || '這個人很懶，還没有个人简介哦~'}</p>
              </section>

              {/* Description */}
              <section>
                <div class="flex items-center justify-between mb-2">
                  <h3 class="text-lg font-bold">介紹</h3>
                  <div class="text-pink-500 text-sm cursor-pointer">查看全部資料</div>
                </div>
                
                <div class="flex gap-2 mb-3">
                  <For each={props.char.tags || ['原创', '纯爱', '校园']}>
                    {(tag) => (
                      <Pill class="!bg-800 !border-gray-600 !rounded-full !text-xs !px-3">{tag}</Pill>
                    )}
                  </For>
                </div>

                <div class="flex items-center gap-1 mb-4 text-xs text-green-500">
                  <div class="flex -space-x-1">
                      <For each={[1,2,3,4,5]}>
                        {() => <div class="w-4 h-4 rounded-full border border-900 bg-green-500 overflow-hidden" />}
                      </For>
                  </div>
                  <span>{detail()?.chatCount || 0} 位玩家正在品鑒</span>
                </div>

                <div class="bg-black/40 rounded-xl p-4 text-sm leading-relaxed text-gray-300">
                  {props.char.description || '暂无描述'}
                </div>
              </section>
            </Show>

            <Show when={tabs.selected() === 1}>
              <section class="space-y-4">
                <div class="flex gap-2">
                  <input 
                    type="text" 
                    value={commentText()} 
                    onInput={(e) => setCommentText(e.currentTarget.value)}
                    placeholder="说点什么吧..."
                    class="flex-1 bg-800 border-none rounded-full px-4 text-sm focus:ring-1 focus:ring-pink-500"
                  />
                  <Button 
                    size="sm" 
                    onClick={submitComment}
                    disabled={isSubmitting() || !commentText().trim()}
                    class="!rounded-full !bg-pink-500 !text-white"
                  >
                    <Send size={16} />
                  </Button>
                </div>

                <div class="space-y-4">
                  <For each={comments()} fallback={<div class="text-center text-500 py-8">暂无评论</div>}>
                    {(comment) => (
                      <div class="flex gap-3">
                        <div class="w-10 h-10 rounded-full overflow-hidden bg-700 flex-shrink-0">
                          <Show when={comment.userAvatar} fallback={<User size={20} class="m-auto mt-2" />}>
                            <img src={getAssetUrl(comment.userAvatar!)} class="w-full h-full object-cover" />
                          </Show>
                        </div>
                        <div class="flex-1">
                          <div class="flex justify-between items-center">
                            <span class="font-bold text-sm">{comment.userName}</span>
                            <span class="text-[10px] text-500">{toDuration(new Date(comment.createdAt))}</span>
                          </div>
                          <p class="text-sm text-gray-300 mt-1">{comment.content}</p>
                        </div>
                      </div>
                    )}
                  </For>
                </div>
              </section>
            </Show>

            <Show when={tabs.selected() === 2}>
              <section class="grid grid-cols-2 gap-4">
                <For each={similarChars()} fallback={<div class="col-span-2 text-center text-500 py-8">暂无相似角色</div>}>
                  {(char) => (
                    <div 
                      class="bg-800 rounded-xl overflow-hidden border border-700 cursor-pointer hover:border-pink-500/50 transition-colors"
                      onClick={() => {
                        // 点击相似角色，关闭当前弹窗并导航（或更新当前弹窗数据）
                        nav(`/chat/${char._id}`) // 示例：直接去聊天，或者你可以根据需求调整
                        props.close()
                      }}
                    >
                      <div class="h-32 w-full overflow-hidden">
                        <Show when={char.avatar}>
                          <img src={getAssetUrl(char.avatar!)} class="w-full h-full object-cover" />
                        </Show>
                      </div>
                      <div class="p-2">
                        <div class="font-bold text-sm truncate">{char.name}</div>
                        <div class="text-[10px] text-500 truncate">{char.description || '暂无描述'}</div>
                      </div>
                    </div>
                  )}
                </For>
              </section>
            </Show>
          </div>

          {/* Footer Button */}
          <div class="sticky bottom-0 p-4 bg-900/80 backdrop-blur-md flex gap-3">
            <Button 
              onClick={toggleFavorite}
              class={`!py-3 !px-6 !rounded-full font-bold text-lg border ${detail()?.isFavorited ? '!bg-pink-500/20 !border-pink-500 !text-pink-500' : '!bg-800 !border-gray-600 !text-gray-400'}`}
            >
              <Star size={24} fill={detail()?.isFavorited ? "currentColor" : "none"} />
            </Button>
            <Button 
              onClick={startChat}
              class="flex-1 !bg-gradient-to-r !from-pink-400 !to-pink-600 !text-white !py-3 !rounded-full font-bold text-lg shadow-lg shadow-pink-500/20"
            >
              开始聊天
            </Button>
          </div>
        </div>
      </Show>
    </Modal>
  )
}
